import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchProgramList } from '../redux/actions/Actions';
import { useNavigate } from 'react-router-dom';
import RequestCallback from '../RequestCallback';
import SEO from '../common/SEO';

/* ── Category filter tags ─────────────────────────────── */
const FILTERS = [
  { id: 'all',     label: 'All Programs' },
  { id: 'boot',    label: 'Bootcamp'     },
  { id: 'cert',    label: 'Certification'},
  { id: 'career',  label: 'Career'       },
  { id: 'industry',label: 'Industry'     },
];

function getFilter(name = '') {
  const n = name.toUpperCase();
  if (n.includes('BOOT'))       return 'boot';
  if (n.includes('CERT'))       return 'cert';
  if (n.includes('CAREER'))     return 'career';
  if (n.includes('INDUSTRY'))   return 'industry';
  return 'all';
}

const ACCENT = '#2563EB';

const DEDICATED_ROUTES = {
  1: '/programs/coding-bootcamp',
  2: '/programs/developer-full-time',
  3: '/programs/internship',
  4: '/programs/esprp',
};
const getProgramRoute = (id) => DEDICATED_ROUTES[id] || `/programDetail/${id}`;

/* Blue SVG icons — one per slot, all same blue */
const ICONS = [
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>,
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>,
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>,
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>,
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg>,
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
];

/* ── Featured card (first program — large) ────────────── */
const FeaturedCard = ({ item, icon, onEnroll }) => (
  <div style={s.featured}
    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
    onMouseLeave={e => e.currentTarget.style.transform = 'none'}
  >
    <div style={{ ...s.featuredAccentBar, background: ACCENT }} />
    <div style={s.featuredInner}>
      <div style={s.featuredLeft}>
        <h2 style={s.featuredTitle}>{item.program_name}</h2>
        <p style={s.featuredDesc}>
          {item.program_details?.slice(0, 180) || 'A comprehensive industry-grade program designed to make you job-ready with real skills.'}
          {item.program_details?.length > 180 ? '…' : ''}
        </p>
        <div style={s.featuredMeta}>
          <span style={s.metaPill}>Industry Certificate</span>
          <span style={s.metaPill}>Live Sessions</span>
          <span style={s.metaPill}>Job Support</span>
        </div>
        <div style={s.featuredActions}>
          <button style={s.featuredEnrollBtn} onClick={() => onEnroll(item)}
            onMouseEnter={e => e.currentTarget.style.background='#1D4ED8'}
            onMouseLeave={e => e.currentTarget.style.background='#2563EB'}>
            Enroll Now
          </button>
          <Link to={getProgramRoute(item.program_id)} style={s.outlineBtn}>
            View Details →
          </Link>
        </div>
      </div>
      <div style={s.featuredRight}>
        <div style={s.featuredIconBox}>{icon}</div>
        {item.cost && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <div style={{ fontSize: 13, color: '#94A3B8', fontWeight: 500 }}>Program Fee</div>
            <div style={s.featuredPrice}>₹{Number(item.cost).toLocaleString()}</div>
          </div>
        )}
      </div>
    </div>
  </div>
);

/* ── Regular card ─────────────────────────────────────── */
const ProgramCardItem = ({ item, icon, onEnroll }) => {
  const points = (item.program_details || '')
    .split(/\n|\.\s+/).filter(p => p.trim().length > 10).slice(0, 3)
    .map(p => p.trim().replace(/\.$/, ''));
  if (!points.length) points.push('Comprehensive curriculum', 'Live mentorship', 'Job placement support');

  return (
    <div style={s.card}
      onMouseEnter={e => { e.currentTarget.style.transform='translateY(-6px)'; e.currentTarget.style.boxShadow='0 16px 48px rgba(37,99,235,0.2)'; e.currentTarget.style.borderColor='rgba(37,99,235,0.35)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 4px 20px rgba(0,0,0,0.25)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.08)'; }}
    >
      <div style={{ height: 4, background: ACCENT, borderRadius:'12px 12px 0 0' }} />
      <div style={s.cardBody}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ ...s.cardTitle, marginBottom: 0 }}>{item.program_name}</h3>
          <div style={s.iconBox}>{icon}</div>
        </div>
        <ul style={s.pointList}>
          {points.map((pt, i) => (
            <li key={i} style={s.point}>
              <span style={{ color: ACCENT, fontWeight: 700, flexShrink: 0 }}>✓</span>
              <span>{pt}</span>
            </li>
          ))}
        </ul>
        <div style={s.cardFooter}>
          <span style={s.price}>
            {item.cost ? `₹${Number(item.cost).toLocaleString()}` : 'Contact us'}
          </span>
          <Link to={getProgramRoute(item.program_id)} style={s.detailLink}>Details →</Link>
        </div>
        <button style={{ ...s.enrollBtn, marginTop: 12 }} onClick={() => onEnroll(item)}
          onMouseEnter={e => e.currentTarget.style.background='#1D4ED8'}
          onMouseLeave={e => e.currentTarget.style.background='#2563EB'}>
          Enroll Now
        </button>
      </div>
    </div>
  );
};

/* ── Main Programs page ───────────────────────────────── */
const Programs = () => {
  const dispatch      = useDispatch();
  const navigate      = useNavigate();
  const fetchPrograms = useSelector((s) => s.fetchAllPrograms.programs);

  const [activeFilter, setActiveFilter] = useState('all');
  const [showAll,      setShowAll]      = useState(false);
  const [showApply, setShowApply]       = useState(false);
  const [applyProgram, setApplyProgram] = useState('');

  useEffect(() => { dispatch(fetchProgramList()); }, [dispatch]);

  const filtered = activeFilter === 'all'
    ? fetchPrograms
    : fetchPrograms.filter(p => getFilter(p.program_name) === activeFilter);

  const displayLimit = showAll ? filtered.length : 7;
  const featured     = filtered[0];
  const rest         = filtered.slice(1, displayLimit);

  const handleEnroll = (item) => {
    setApplyProgram(item.program_name);
    setShowApply(true);
  };

  return (
    <div style={{ background: '#050D1E', minHeight: '100vh', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <SEO
        title="Programs"
        path="/programs"
        description="Explore UPL Snipe's industry-grade tech training programs — Coding Bootcamp, Developer Full Time, Internship, and ESPRP. Real projects, expert mentors, and placement support."
        keywords="tech programs, coding bootcamp, developer training, internship program, ESPRP, software training courses, IT programs India"
      />
      <style>{`
        @media (max-width: 900px) {
          #prog-grid { grid-template-columns: repeat(2,1fr) !important; }
          #prog-featured-inner { flex-direction: column !important; padding: 24px 20px !important; }
          #prog-featured-right { display: none !important; }
        }
        @media (max-width: 540px) {
          #prog-grid { grid-template-columns: 1fr !important; }
          #prog-filter-row { flex-direction: column !important; align-items: flex-start !important; }
        }
      `}</style>

      {/* ── Hero banner ── */}
      <div style={s.hero}>
        <div style={s.heroBg} />
        <div style={s.heroContent}>
          <span style={s.heroEyebrow}>ALL PROGRAMS</span>
          <h1 style={s.heroH1}>Find Your Perfect Program</h1>
          <p style={s.heroP}>
            Industry-grade programs built for the real world. Choose your path and start building the skills employers want.
          </p>
          <div style={s.heroStats}>
            {[{ v: `${fetchPrograms.length || 8}`, l: 'Programs' }, { v: '1k+', l: 'Students' }, { v: '4.8★', l: 'Rating' }].map(st => (
              <div key={st.l} style={s.heroStat}>
                <div style={s.heroStatV}>{st.v}</div>
                <div style={s.heroStatL}>{st.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={s.main}>

        {/* ── Filter bar ── */}
        <div id="prog-filter-row" style={s.filterRow}>
          <div style={s.filters}>
            {FILTERS.map(f => (
              <button key={f.id} onClick={() => { setActiveFilter(f.id); setShowAll(false); }}
                style={{ ...s.filterBtn, ...(activeFilter === f.id ? s.filterActive : {}) }}>
                {f.label}
                <span style={{ ...s.filterCount, background: activeFilter === f.id ? '#fff' : '#E2E8F0', color: activeFilter === f.id ? '#2563EB' : '#64748B' }}>
                  {f.id === 'all' ? fetchPrograms.length : fetchPrograms.filter(p => getFilter(p.program_name) === f.id).length}
                </span>
              </button>
            ))}
          </div>
          <p style={{ fontSize: 14, color: '#64748B', fontWeight: 500 }}>
            Showing <strong style={{ color: '#93C5FD' }}>{Math.min(displayLimit, filtered.length)}</strong> of <strong style={{ color: '#93C5FD' }}>{filtered.length}</strong> programs
          </p>
        </div>

        {fetchPrograms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#64748B', fontSize: 16 }}>Loading programs…</div>
        ) : (
          <>
            {/* ── Featured (first program) ── */}
            {featured && (
              <div style={{ marginBottom: 40 }}>
                <div style={s.sectionLabel}>Featured Program</div>
                <FeaturedCard item={featured} icon={<svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>} onEnroll={handleEnroll} />
              </div>
            )}

            {/* ── Grid ── */}
            {rest.length > 0 && (
              <div>
                <div style={s.sectionLabel}>Explore Programs</div>
                <div id="prog-grid" style={s.grid}>
                  {rest.map((item, i) => (
                    <ProgramCardItem key={item.program_id} item={item}
                      icon={ICONS[(i + 1) % ICONS.length]}
                      onEnroll={handleEnroll} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Show more / less ── */}
            {filtered.length > 6 && (
              <div style={{ textAlign: 'center', marginTop: 48 }}>
                {!showAll ? (
                  <button style={s.viewAllBtn} onClick={() => setShowAll(true)}>
                    View All {filtered.length} Programs ↓
                  </button>
                ) : (
                  <button style={s.viewAllBtn} onClick={() => setShowAll(false)}>
                    Show Less ↑
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* ── CTA banner ── */}
        <div style={s.cta}>
          <div style={s.ctaInner}>
            <h3 style={s.ctaH}>Not sure which program is right for you?</h3>
            <p style={s.ctaP}>Talk to our advisors and we'll help you choose the perfect path for your career goals.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/request-callback" style={s.ctaBtn}>Talk to an Advisor →</Link>
              <button onClick={() => { setApplyProgram(''); setShowApply(true); }} style={{ ...s.ctaBtnOutline, cursor: 'pointer' }}>Enroll Now</button>
            </div>
          </div>
        </div>

      </div>

      {showApply && (
        <RequestCallback
          isModal
          onClose={() => { setShowApply(false); setApplyProgram(''); }}
          defaultProgram={applyProgram}
        />
      )}
    </div>
  );
};

/* ── Styles ─────────────────────────────────────────────── */
const s = {
  hero: {
    position: 'relative', overflow: 'hidden',
    background: 'linear-gradient(135deg,#050D1E 0%,#091525 40%,#0C1B38 70%,#0A1628 100%)',
    padding: '72px 5% 80px', textAlign: 'center',
  },
  heroBg: {
    position:'absolute', inset:0,
    backgroundImage:'linear-gradient(rgba(59,130,246,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,0.05) 1px,transparent 1px)',
    backgroundSize:'48px 48px', pointerEvents:'none',
  },
  heroContent: { position:'relative', zIndex:1, maxWidth:700, margin:'0 auto' },
  heroEyebrow: { fontSize:12, fontWeight:700, letterSpacing:'0.14em', color:'rgba(255,255,255,0.75)', textTransform:'uppercase' },
  heroH1: { fontSize:'clamp(32px,5vw,60px)', fontWeight:800, color:'#FFFFFF', letterSpacing:'-0.03em', lineHeight:1.1, margin:'14px 0 18px' },
  heroP:  { fontSize:17, color:'rgba(255,255,255,0.85)', lineHeight:1.7, marginBottom:40 },
  heroStats: { display:'flex', justifyContent:'center', gap:40 },
  heroStat:  { textAlign:'center' },
  heroStatV: { fontSize:30, fontWeight:800, color:'#FFFFFF', letterSpacing:'-0.02em' },
  heroStatL: { fontSize:12, color:'rgba(255,255,255,0.65)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:2 },

  main: { maxWidth:1200, margin:'0 auto', padding:'48px 5% 80px' },

  filterRow: { display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12, marginBottom:40 },
  filters:   { display:'flex', gap:8, flexWrap:'wrap' },
  filterBtn: {
    display:'inline-flex', alignItems:'center', gap:8,
    padding:'8px 16px', border:'1.5px solid rgba(255,255,255,0.1)', borderRadius:50,
    background:'rgba(255,255,255,0.05)', color:'#94A3B8', fontSize:14, fontWeight:500, cursor:'pointer',
    transition:'all 0.2s',
  },
  filterActive: { background:'#2563EB', borderColor:'#2563EB', color:'#FFFFFF', fontWeight:700 },
  filterCount: { fontSize:12, fontWeight:700, padding:'1px 7px', borderRadius:50 },

  sectionLabel: { fontSize:13, fontWeight:700, letterSpacing:'0.06em', color:'#475569', textTransform:'uppercase', marginBottom:16 },

  /* Featured */
  featured: {
    background:'#0C1B38', borderRadius:16, border:'1px solid rgba(255,255,255,0.08)',
    boxShadow:'0 4px 24px rgba(0,0,0,0.3)', overflow:'hidden',
    transition:'transform 0.25s ease',
  },
  featuredAccentBar: { height:5 },
  featuredInner: { display:'flex', alignItems:'center', justifyContent:'space-between', gap:40, padding:'32px 40px', flexWrap:'wrap' },
  featuredLeft:  { flex:1 },
  featuredRight: { flexShrink:0, textAlign:'center' },
  featuredTitle: { fontSize:'clamp(22px,3vw,34px)', fontWeight:800, color:'#F1F5F9', letterSpacing:'-0.03em', lineHeight:1.2, margin:'16px 0 12px' },
  featuredDesc:  { fontSize:15, color:'#94A3B8', lineHeight:1.7, marginBottom:20, maxWidth:520 },
  featuredMeta:  { display:'flex', gap:10, flexWrap:'wrap', marginBottom:24 },
  metaPill: { fontSize:12, fontWeight:600, color:'#60A5FA', background:'rgba(37,99,235,0.15)', padding:'5px 12px', borderRadius:50, border:'1px solid rgba(37,99,235,0.3)' },
  featuredActions: { display:'flex', gap:12, flexWrap:'wrap' },
  featuredIconBox: {
    width:160, height:160, borderRadius:24,
    background:'rgba(37,99,235,0.1)', border:'2px solid rgba(37,99,235,0.25)',
    display:'flex', alignItems:'center', justifyContent:'center',
  },
  featuredPrice: { fontSize:28, fontWeight:800, letterSpacing:'-0.02em', marginTop:4, color:'#60A5FA' },

  /* Grid */
  grid: { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:24, id:'prog-grid' },

  /* Card */
  card: {
    background:'#0C1B38', borderRadius:12, border:'1px solid rgba(255,255,255,0.08)',
    boxShadow:'0 4px 20px rgba(0,0,0,0.25)', overflow:'hidden',
    display:'flex', flexDirection:'column', transition:'transform 0.25s ease,box-shadow 0.25s ease',
  },
  cardBody: { padding:'20px 24px 24px', flex:1, display:'flex', flexDirection:'column' },
  cardTop:  { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 },
  cardTitle: { fontSize:17, fontWeight:800, color:'#F1F5F9', letterSpacing:'-0.02em', lineHeight:1.3, margin:'0 0 14px' },
  pointList: { listStyle:'none', padding:0, margin:'0 0 16px', flex:1, display:'flex', flexDirection:'column', gap:7 },
  point:     { display:'flex', alignItems:'flex-start', gap:8, fontSize:13, color:'#94A3B8', lineHeight:1.5 },
  cardFooter:{ display:'flex', alignItems:'center', justifyContent:'space-between' },
  price:     { fontSize:20, fontWeight:800, letterSpacing:'-0.02em', color:'#60A5FA' },
  detailLink:{ fontSize:13, fontWeight:600, color:'#60A5FA', textDecoration:'none' },

  badge: {
    fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:50,
    letterSpacing:'0.05em', textTransform:'uppercase',
    background:'rgba(37,99,235,0.15)', color:'#60A5FA', border:'1px solid rgba(37,99,235,0.3)',
  },
  iconBox: {
    width:40, height:40, borderRadius:10,
    background:'rgba(37,99,235,0.1)', border:'1px solid rgba(37,99,235,0.25)',
    display:'flex', alignItems:'center', justifyContent:'center',
  },
  enrollBtn: {
    padding:'13px 28px', border:'none', borderRadius:10, color:'#fff',
    background:'#2563EB', fontSize:14, fontWeight:700, cursor:'pointer',
    letterSpacing:'0.03em', transition:'background 0.2s', width:'100%',
    display:'block',
  },
  featuredEnrollBtn: {
    padding:'12px 32px', border:'none', borderRadius:10, color:'#fff',
    background:'#2563EB', fontSize:15, fontWeight:700, cursor:'pointer',
    letterSpacing:'0.03em', transition:'background 0.2s', display:'inline-block',
    whiteSpace:'nowrap',
  },
  outlineBtn: {
    padding:'11px 24px', borderRadius:10, fontWeight:700, fontSize:14,
    textDecoration:'none', border:'2px solid rgba(96,165,250,0.5)', color:'#93C5FD',
    display:'inline-flex', alignItems:'center', transition:'opacity 0.2s',
    whiteSpace:'nowrap',
  },

  viewAllBtn: {
    padding:'14px 40px', background:'transparent', border:'1.5px solid rgba(37,99,235,0.5)',
    borderRadius:50, color:'#60A5FA', fontSize:15, fontWeight:700, cursor:'pointer',
    transition:'all 0.2s',
  },

  /* CTA */
  cta: {
    marginTop:64,
    background:'linear-gradient(135deg,#0C1B38 0%,#0D1E3D 50%,#162650 100%)',
    borderRadius:20, overflow:'hidden', padding:'56px 5%',
    textAlign:'center', position:'relative',
    border:'1px solid rgba(37,99,235,0.2)',
  },
  ctaInner: { maxWidth:560, margin:'0 auto', position:'relative', zIndex:1 },
  ctaH: { fontSize:'clamp(22px,3vw,34px)', fontWeight:800, color:'#F1F5F9', letterSpacing:'-0.02em', margin:'0 0 12px' },
  ctaP: { fontSize:16, color:'#94A3B8', lineHeight:1.7, marginBottom:32 },
  ctaBtn: {
    display:'inline-flex', alignItems:'center', padding:'14px 36px',
    background:'#2563EB', color:'#FFFFFF', fontWeight:700, fontSize:15,
    borderRadius:10, textDecoration:'none', transition:'opacity 0.2s',
    boxShadow:'0 4px 16px rgba(37,99,235,0.35)',
  },
  ctaBtnOutline: {
    display:'inline-flex', alignItems:'center', padding:'13px 32px',
    background:'transparent', color:'#93C5FD', fontWeight:700, fontSize:15,
    borderRadius:10, textDecoration:'none', border:'2px solid rgba(96,165,250,0.35)',
    transition:'background 0.2s',
  },
};

export default Programs;
